import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  accessToken: string | null;
  accessTokenExpiry: Date | null;
  isAuthenticated: boolean;
  userId: number | null;
  setAccessToken: (token: string, expiryInSeconds: number) => void;
  setUserId: (userId: number | null) => void;
  clearAuth: () => void;
  isTokenExpired: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      accessTokenExpiry: null,
      isAuthenticated: false,
      userId: null,

      setAccessToken: (token: string, expiryInSeconds: number) => {
        // Store token directly (bypass SecureTokenManager)
        sessionStorage.setItem('tahta_access_token', token);
        
        const expiryDate = new Date(Date.now() + expiryInSeconds * 1000);
        set({
          accessToken: token,
          accessTokenExpiry: expiryDate,
          isAuthenticated: true,
        });
      },
      
      setUserId: (userId: number | null) => {
        set({ userId });
      },

      clearAuth: () => {
        // Clear from storage directly
        sessionStorage.removeItem('tahta_access_token');
        
        set({
          accessToken: null,
          accessTokenExpiry: null,
          isAuthenticated: false,
          userId: null,
        });
      },

      isTokenExpired: () => {
        const { accessTokenExpiry } = get();
        if (!accessTokenExpiry) return true;
        
        // Convert string date back to Date object if needed (after rehydration)
        const expiry = typeof accessTokenExpiry === 'string' 
          ? new Date(accessTokenExpiry) 
          : accessTokenExpiry;
          
        return new Date() > expiry;
      },
    }),
    {
      name: 'auth-storage',
      // Custom storage to handle Date serialization
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          
          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              accessTokenExpiry: state.accessTokenExpiry 
                ? new Date(state.accessTokenExpiry) 
                : null
            }
          };
        },
        setItem: (name, value) => {
          const { state } = value;
          const serialized = JSON.stringify({
            state: {
              ...state,
              accessTokenExpiry: state.accessTokenExpiry?.toISOString() || null
            }
          });
          localStorage.setItem(name, serialized);
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        }
      }
    }
  )
);